import classNames from "classnames";
import { observer } from "mobx-react-lite";
import React, { useCallback, useContext } from "react";
import { Alert } from "reactstrap";
import { GalleryItemType } from "../../app/models/content";
import { RootStoreContext } from "../../app/stores/rootStore";
import ProtectedVideo from "../common/ProtectedVideo";
import ProtectedZwibblerDiv from "../common/ProtectedZwibblerDiv";
import VideoControlsBar from "./VideoControlsBar";
import { toast } from "react-toastify";

interface IParams {
  itemType: GalleryItemType;
  itemUrl: string;
  itemIndex: number;
  fileId: number;
  isCurrentlyVisible: boolean;
  isThumbnailsBarOpen: boolean;
  isFullscreen: boolean;
  onDoubleClick: () => void;
}

const CustomGalleryItem: React.FC<IParams> = ({
  itemType,
  itemUrl,
  itemIndex,
  fileId,
  isCurrentlyVisible,
  isThumbnailsBarOpen,
  isFullscreen,
  onDoubleClick,
}) => {
  const rootStore = useContext(RootStoreContext);
  const { broadcastFile } = rootStore.tenantStore;
  const {
    ZwibblerOverlayResize,
    isOneButtonEnabled,
    zwibblerDivStyle,
    createZwibblerContext,
    initializeZwibblerContainer,
  } = rootStore.zwibblerStore;
  const { isOnair, canUseScreens } = rootStore.screensStore;
  const { setZoomPortionEnabled, isZoomPortionEnabled } = rootStore.playerStore;
  const handleDoubleClick = () => {
    if (!isOneButtonEnabled) onDoubleClick();
  };

  const handleZwibblerContext = useCallback(() => {

    setZoomPortionEnabled(false);
    const divId = `zwibblerItem_${itemIndex}`;
    return createZwibblerContext(divId, fileId);
  }, [createZwibblerContext, itemIndex, fileId]);

  const handleSharing = useCallback(() => {
    broadcastFile({ type: itemType, fileId: fileId });
  }, [broadcastFile, itemType, fileId]);

  const getHeightCssClass = () => {
    return `image-gallery-height${isThumbnailsBarOpen ? "-thumbnails" : ""}${
      isFullscreen ? "-fullscreen" : ""
    }`;
  };

  const renderItem = (type: GalleryItemType) => {
    switch (type) {
      case "image":
        const zwibblerDivId = `zwibblerItem_${itemIndex}`;
        return (
          <>
            <ProtectedZwibblerDiv
              url={itemUrl}
              addZwibblerLayer={isCurrentlyVisible}
              zwibblerDivId={zwibblerDivId}
              zwibblerStyle={zwibblerDivStyle}
              createZwibblerCtx={handleZwibblerContext}
              onRendering={handleSharing}
              onImageLoad={initializeZwibblerContainer}
              resizeHandler={ZwibblerOverlayResize}
              className={classNames(
                "image-gallery-image",
                "image-gallery-common",
                getHeightCssClass()
              )}
            />
          </>
        );

      case "video":
        return (
          <>
            <ProtectedVideo
              url={itemUrl}
              isRendered={isCurrentlyVisible}
              onRendering={handleSharing}
              className={classNames(
                "image-gallery-image",
                "repositoryVideo",
                getHeightCssClass()
              )}
            />
            {isOnair && canUseScreens && <VideoControlsBar />}
          </>
        );
      default:
        return <Alert>{`itemType ${itemType} not implemented`}</Alert>;
    }
  };

  return (
    <div onDoubleClick={() => handleDoubleClick()}>{renderItem(itemType)}</div>
  );
};

export default observer(CustomGalleryItem);
